import {
  Button,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Separator,
  showNotification,
} from 'platform/components';
import {Box, Center, Grid, HStack, Show, Space, Text, VStack} from 'platform/foundation';
import {z} from 'zod';

import {Trans} from 'react-i18next';

import {isNil, isNotNil} from 'ramda';

import {
  EmailCredentialsOwnerType,
  SmtpEmailProviderCode,
  useCreateEmailSmtpCredentialsMutation,
  usePatchEmailSmtpCredentialsMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {getSmtpProviderDocumentationLink} from '../utils/getSmtpProviderDocumentationLink';
import {getSmtpProviderLogo} from '../utils/getSmtpProviderLogo';

interface EmailServiceFormProps {
  onChangeProvider?: () => void;
  providerCode?: SmtpEmailProviderCode;
  defaultValues: Partial<EmailServiceFormSchemaType>;
  id?: string;
  ownerType: EmailCredentialsOwnerType;
}

export function EmailServiceForm(props: EmailServiceFormProps) {
  const isCreating = isNil(props.id);
  const providerCode = props.providerCode ?? 'custom-smtp';

  const [createSmtpCredentials] = useCreateEmailSmtpCredentialsMutation();
  const [patchSmtpCredentials] = usePatchEmailSmtpCredentialsMutation();

  const handleSubmit: FormSubmitHandler<EmailServiceFormSchemaType> = async (values) => {
    const credentialsId = props.id;

    if (isNil(credentialsId)) {
      await createSmtpCredentials({
        createSmtpCredentialsRequestBody: {
          ownerType: props.ownerType,
          password: values.password ?? '',
          ...values,
        },
      })
        .unwrap()
        .then(() => {
          showNotification.success(i18n.t('general.notifications.successfullyCreated'));
          closeCurrentDialog();
        })
        .catch(handleApiError);
    } else {
      await patchSmtpCredentials({
        id: credentialsId,
        patchSmtpCredentialsRequestBody: values,
      })
        .unwrap()
        .then(() => {
          closeCurrentDialog();
        })
        .catch(handleApiError);
    }
  };

  const providerLogo = getSmtpProviderLogo(providerCode);
  const providerDocumentationLink = getSmtpProviderDocumentationLink(providerCode);

  return (
    <Form<EmailServiceFormSchemaType>
      defaultValues={props.defaultValues}
      onSubmit={handleSubmit}
      experimentalZodSchema={isCreating ? CreateEmailServiceFormSchema : EditEmailServiceFormSchema}
    >
      {(control) => (
        <VStack spacing={4}>
          <Show when={isNotNil(providerLogo)}>
            <Center width="100%" height="100%">
              <Space vertical={4} />
              <Box width={40} height={40}>
                <Center width="100%" height="100%">
                  {providerLogo}
                </Center>
              </Box>
            </Center>
          </Show>

          <VStack>
            <Text align="center" size="small" color="secondary">
              <Trans
                i18nKey={`entity.emailService.labels.${providerCode}-documentationMessage`}
                components={{
                  break: <br />,
                  bold: <Text alternative inline size="small" color="secondary" />,
                }}
              />
            </Text>

            <Button
              variant="link"
              title={i18n.t('general.actions.viewDocumentation')}
              onClick={() => window.open(providerDocumentationLink ?? '', '_blank')}
              leftIcon="action/open_in_new"
            />
          </VStack>

          <VStack spacing={4}>
            <FormField
              name="title"
              type="text"
              label={i18n.t('entity.emailService.labels.title')}
              control={control}
              tooltip={i18n.t(`entity.emailService.labels.${providerCode}-titleTooltip`)}
              isRequired
            />

            <FormField
              name="emailAddress"
              type="email"
              label={i18n.t('entity.emailService.labels.emailAddress')}
              tooltip={i18n.t(`entity.emailService.labels.${providerCode}-emailAddressTooltip`)}
              control={control}
              isRequired
            />

            <HStack spacing={4}>
              <Box flex={1}>
                <FormField
                  name="host"
                  type="text"
                  label={i18n.t('entity.emailService.labels.host')}
                  tooltip={i18n.t(`entity.emailService.labels.${providerCode}-hostTooltip`)}
                  control={control}
                  isRequired
                />
              </Box>
              <FormField
                name="port"
                type="number"
                label={i18n.t('entity.emailService.labels.port')}
                tooltip={i18n.t(`entity.emailService.labels.${providerCode}-portTooltip`)}
                control={control}
                isRequired
              />
            </HStack>

            <Grid columns={isCreating ? 2 : 1}>
              <FormField
                name="username"
                type="text"
                label={i18n.t('entity.emailService.labels.userName')}
                tooltip={i18n.t(`entity.emailService.labels.${providerCode}-userNameTooltip`)}
                control={control}
                isRequired
              />

              <Show when={isCreating}>
                <FormField
                  name="password"
                  type="password"
                  label={i18n.t('entity.emailService.labels.password')}
                  tooltip={i18n.t(`entity.emailService.labels.${providerCode}-passwordTooltip`)}
                  control={control}
                  isRequired
                />
              </Show>
            </Grid>

            <Separator spacing={0} />

            <HStack justify="space-between">
              <Box>
                <Show when={isNotNil(props.onChangeProvider)}>
                  <Button
                    title={i18n.t('general.actions.back')}
                    variant="ghostLink"
                    leftIcon="navigation/arrow_back"
                    onClick={props.onChangeProvider}
                  />
                </Show>
              </Box>

              <HStack spacing={2}>
                <Button
                  title={i18n.t('general.actions.discard')}
                  variant="secondary"
                  onClick={closeCurrentDialog}
                />
                <FormButton
                  control={control}
                  type="submit"
                  title={
                    isCreating
                      ? i18n.t('general.actions.create')
                      : i18n.t('general.actions.saveChanges')
                  }
                />
              </HStack>
            </HStack>
          </VStack>
        </VStack>
      )}
    </Form>
  );
}

const EditEmailServiceFormSchema = z.object({
  title: z.string().min(1),
  host: z.string().min(1),
  port: z.number().min(1).max(65535),
  username: z.string().min(1),
  emailAddress: z.string().email(),
});

const CreateEmailServiceFormSchema = EditEmailServiceFormSchema.extend({
  password: z.string().min(1),
});

export type EmailServiceFormSchemaType = {
  title: string;
  host: string;
  port: number;
  username: string;
  emailAddress: string;
  password?: string;
};
